import { apiFetch } from "./apiFetch";

// Reports scans the browser SAW but never delivered to the BE.
//
// A dropped scan currently leaves no trace anywhere: no request, no log row,
// no light cue — the operator just sees nothing happen and scans again, which
// reads as "the light is slow". (2026-08-19, Pembroke P&S: a 17s gap
// containing only the 5s /plan poll, so the page was alive and simply
// swallowed the scan.) These events are what make that case provable.
//
// Rules for this module: it sits next to the scan path, so it must never
// throw, never block, and never retry hard enough to matter.

export type ScanAnomaly =
    /** Scanner keystrokes went to a focused field / the on-screen keyboard,
     *  so the global listener never buffered them. */
    | "swallowed"
    /** Scan completed while the input was disabled; parked for replay. */
    | "held"
    /** A held scan outlived HELD_SCAN_TTL_MS and was discarded. */
    | "held_expired"
    /** handleScan returned early because a request was still in flight. */
    | "dropped_loading"
    /** Scanned while a placement was showing and it wasn't the slot barcode. */
    | "wrong_location"
    /** Unload: the scanned slot was refused client-side (unknown, unfinished,
     *  or already unloaded), so no request was made. */
    | "slot_not_ready"
    /** The scan matched a known barcode by case alone — the scanner's wedge
     *  lost SHIFT on the opening characters of the burst. Recovered and
     *  accepted; this event is the only trace, since the operator sees a
     *  working scan. A device that produces these is degrading. */
    | "case_recovered"
    /** A case-only match existed but two or more candidates differed only by
     *  case, so the scan was refused rather than guessed. */
    | "case_ambiguous";

interface QueuedEvent {
    event: ScanAnomaly;
    module: string;
    barcode?: string | null;
    waited_ms?: number | null;
    detail?: Record<string, unknown> | null;
    at: string;
    path: string;
}

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const FLUSH_DEBOUNCE_MS = 1500;
const MAX_QUEUE = 50;

const flush = () => {
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    if (queue.length === 0) return;
    const events = queue;
    queue = [];
    // Fire and forget. A failed telemetry post must never surface to the
    // operator or retry into the next scan.
    apiFetch("/client-telemetry/scan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events }),
        keepalive: true,
    }).catch(() => { /* diagnostics only */ });
};

/** Record one anomaly. Batched; safe to call from a keystroke handler. */
export const recordScanAnomaly = (
    event: ScanAnomaly,
    module: string,
    detail?: Record<string, unknown> | null,
    opts?: { barcode?: string | null; waited_ms?: number | null },
): void => {
    try {
        // Drop rather than grow without bound — a wedged device must not
        // accumulate megabytes of diagnostics it can never send.
        if (queue.length >= MAX_QUEUE) return;
        queue.push({
            event,
            module,
            barcode: opts?.barcode ?? null,
            waited_ms: opts?.waited_ms ?? null,
            detail: detail ?? null,
            at: new Date().toISOString(),
            path: typeof window !== "undefined" ? window.location.pathname : "",
        });
        if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_DEBOUNCE_MS);
    } catch { /* diagnostics only */ }
};

// Anything still queued when the screen goes away would otherwise be lost —
// and a scan dropped just before the operator navigated is exactly the kind
// worth keeping. `keepalive` on the POST lets it outlive the page.
if (typeof window !== "undefined") {
    window.addEventListener("pagehide", flush);
    window.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") flush();
    });
}

/** Test-only: drain state between cases. */
export const __resetScanTelemetryForTests = () => {
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    queue = [];
};

/** Test-only: what is currently queued. */
export const __pendingScanTelemetryForTests = () => [...queue];
